import { useLogto } from '@logto/react';
import { useEffect, useMemo, useState } from 'react';
import { createApiClient, type ManifestDeparture } from '../api/client';
import { addDays, todayInCaboVerde } from '../caboVerde';
import { bookingStatusLabel } from '../customers';
import { ErrorBanner } from '../ui/ErrorBanner';
import { channelLabel, formatCaboVerdeTime, formatDayLabel, telHref } from '../ui/format';

export function Manifest() {
  const { getAccessToken } = useLogto();
  const api = useMemo(() => createApiClient(getAccessToken), [getAccessToken]);
  const [date, setDate] = useState(() => todayInCaboVerde());
  const [departures, setDepartures] = useState<ManifestDeparture[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Guard against a slow response for an earlier day landing after a later one and overwriting it under the
  // wrong date: when the day changes the previous request is marked stale and its result is dropped.
  useEffect(() => {
    let active = true;
    api
      .getManifest(date)
      .then((res) => {
        if (active) {
          setDepartures(res.departures);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (active) setError((err as Error).message);
      });
    return () => {
      active = false;
    };
  }, [api, date]);

  return (
    <section className="page">
      <header className="page-head">
        <h1>Manifesto</h1>
        <p className="subtle">Quem está em cada partida do dia.</p>
      </header>

      <div className="manifest-toolbar">
        <button className="btn" onClick={() => setDate((d) => addDays(d, -1))}>← Dia anterior</button>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value || todayInCaboVerde())} aria-label="Dia" />
        <button className="btn" onClick={() => setDate((d) => addDays(d, 1))}>Dia seguinte →</button>
      </div>
      <p className="subtle manifest-day">{formatDayLabel(date)}</p>

      <ErrorBanner message={error} />

      {departures.length === 0 && !error ? (
        <div className="card subtle">Sem partidas neste dia.</div>
      ) : null}

      {departures.map((d) => (
        <section className="card manifest-card" key={`${d.tourId}:${d.startsAt}`}>
          <div className="manifest-head">
            <div>
              <div className="manifest-time">{formatCaboVerdeTime(d.startsAt)}</div>
              <div className="subtle">{d.tourTitle}</div>
            </div>
            <div className="manifest-seats">
              <span className="manifest-taken">{d.seatsTaken}{d.capacity != null ? `/${d.capacity}` : ''}</span>
              <span className="subtle">{d.capacity != null ? `${d.seatsFree} livres` : 'lugares'}</span>
            </div>
          </div>
          {d.bookings.length === 0 ? (
            <p className="subtle">Sem reservas.</p>
          ) : (
            <ul className="manifest-pax">
              {d.bookings.map((b) => (
                <li key={b.bookingId}>
                  <div className="manifest-pax-main">
                    <span className="manifest-pax-name">{b.customerName ?? '—'}</span>
                    <span className="chip">{b.pax} pax</span>
                    {b.status !== 'Confirmed' ? <span className="badge badge-muted">{bookingStatusLabel(b.status)}</span> : null}
                  </div>
                  <div className="manifest-pax-meta subtle">
                    <span>{channelLabel(b.channel)}</span>
                    {b.phone ? <a href={telHref(b.phone)}>{b.phone}</a> : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </section>
  );
}
